import { getItemLocalMediaStatesWith, syncClipWith, tryMakeDownloadIdentityForClipInfo } from '@mimir/client-common/upload/premiere';
import { vueSet } from '../compat/vueCompat';
import { logErrorAndShowToast } from '../util/uiError';
import { clipInfosArray, updatePremiereClipInfo } from './premiere';
import { startPollingTransferProgress } from './transferProgress';
import { makeDownloadOperation } from '../premiere/makeSyncOp';
import { folders } from './folders';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { UploadContext, UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { PremiereClipInfoMap } from '@mimir/client-common/fileSync/premiereTypes';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { storeDispatchLoadStorageProfile } from '@mimir/web/desktop/storageProfile';
import type { ProjectOps } from '@mimir/client-common/fileSync/projectOps';
import type { UploadOps } from '@mimir/client-common/fileSync/uploadOps';
import type { DownloadAndRelinkOps } from '@mimir/client-common/fileSync/downloadAndRelinkOps';
import { invariant } from '@mimir/common/util/invariant';


export async function mediaRestored(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    downloadPath: string,
    isStandaloneMode: boolean,
    isProxyMode: boolean,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  itemId: string,
) {
  const queuedDownload = services.ongoingDownloadOps.getOngoingDownload(itemId);

  if (queuedDownload) {
    try {
      queuedDownload.highresIsOffline = false;
      vueSet(queuedDownload, 'restoreStatus', undefined);
      services.ongoingDownloadOps.updateOngoingDownload(queuedDownload);
      const clipInfo = clipInfosArray.value.find((c) => c && c.mimirItemId === itemId);
      // Sync ClipInfo of the corresponding item
      if (clipInfo && clipInfo.mimirItem) {
        const dlId = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
        invariant(dlId !== null, 'Unexpected null dlId since clipInfo.mimirItem is defined');
        clipInfo.mimirItemLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlId);
        updatePremiereClipInfo(ongoingUploads, clipInfo);
      }

      const profile = await storeDispatchLoadStorageProfile(
        services.applicationContext,
        false,
        ongoingUploads,
        config.downloadPath,
        services.localStateOps,
      );
      if (!profile) {
        // User canceled
        return;
      }

      if (profile.storageId) {
        startPollingTransferProgress(services, config, ongoingUploads, profile.storageId);
      }
    } catch (err) {
      logErrorAndShowToast('Could not restore media', err, 'ex in mediaRestored');
    }
  } else {
    services.logger.info(`Item ${itemId} has been restored without a transfer request`);
    const clipInfo = clipInfosArray.value.find((c) => c && c.mimirItemId === itemId);
    // Sync ClipInfo of the corresponding item
    if (clipInfo && clipInfo.mimirItem) {
      services.logger.info(`Initiating import of highres for restored item ${itemId}`);
      const dlId = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
      invariant(dlId !== null, 'Unexpected null dlId since clipInfo.mimirItem is defined');
      clipInfo.mimirItemLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlId);
      updatePremiereClipInfo(ongoingUploads, clipInfo);
      const dlOp = makeDownloadOperation(config, 'highres', false);
      await syncClipWith(
      services,
      config, false, premiereClipInfos, ongoingUploads, clipInfo, dlOp, null, undefined, folders.workFolder ? folders.workFolder.id : undefined, undefined);
    }
  }
}
